<script setup lang="ts">
  import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    ModalSectionHeaderDescription
  } from '@gat-ship/vue-components'
  import { IconChevronDown } from '@gat-ship/icons'
  import { useUser } from '@gat-ship/user'
  import { useLocalizationSettings } from '../composables/useLocalizationSettings'

  const { userPreferences } = useUser()

  const {
    dateOptions,
    weekNumberOptions,
    firstDayOptions,
    numberOptions,
    findTitle,
    setDateFormat,
    setWeekNumberFormat,
    setFirstDayOfWeek,
    setNumberFormat
  } = useLocalizationSettings()
</script>

<template>
  <div class="flex flex-col gap-6">
    <ModalSection>
      <ModalSectionHeader class="pt-1">
        <ModalSectionHeaderTitle>Localization</ModalSectionHeaderTitle>
        <ModalSectionHeaderDescription>
          Date, time, and number formatting preferences.
        </ModalSectionHeaderDescription>
      </ModalSectionHeader>
      <div class="flex flex-col divide-y">
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Locale</span>
          <span class="text-muted-foreground text-sm">
            {{ userPreferences.systemSettings.value.locale.toString() }}
          </span>
        </div>
        <div class="flex items-center justify-between py-3">
          <span class="text-sm font-medium">Timezone</span>
          <span class="text-muted-foreground text-sm">
            {{ userPreferences.systemSettings.value.timezone }}
          </span>
        </div>
        <div class="flex items-center justify-between gap-4 py-3">
          <span class="text-sm font-medium">Date Format</span>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="sm">
                {{
                  findTitle(
                    dateOptions,
                    userPreferences.getUserPreferences().dateFormat
                  )
                }}
                <IconChevronDown class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup
                :model-value="userPreferences.getUserPreferences().dateFormat"
                @update:model-value="setDateFormat"
              >
                <DropdownMenuRadioItem
                  v-for="opt in dateOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.title }}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div class="flex items-center justify-between gap-4 py-3">
          <span class="text-sm font-medium">Week Numbers</span>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="sm">
                {{
                  findTitle(
                    weekNumberOptions,
                    userPreferences.getUserPreferences().weekNumberFormat
                  )
                }}
                <IconChevronDown class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup
                :model-value="
                  userPreferences.getUserPreferences().weekNumberFormat
                "
                @update:model-value="setWeekNumberFormat"
              >
                <DropdownMenuRadioItem
                  v-for="opt in weekNumberOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.title }}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div class="flex items-center justify-between gap-4 py-3">
          <span class="text-sm font-medium">First Day of Week</span>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="sm">
                {{
                  findTitle(
                    firstDayOptions,
                    userPreferences.getUserPreferences().firstDayOfWeek
                  )
                }}
                <IconChevronDown class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup
                :model-value="
                  userPreferences.getUserPreferences().firstDayOfWeek
                "
                @update:model-value="setFirstDayOfWeek"
              >
                <DropdownMenuRadioItem
                  v-for="opt in firstDayOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.title }}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div class="flex items-center justify-between gap-4 py-3">
          <span class="text-sm font-medium">Number Format</span>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="sm">
                {{
                  findTitle(
                    numberOptions,
                    userPreferences.getUserPreferences().numberFormat
                  )
                }}
                <IconChevronDown class="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuRadioGroup
                :model-value="userPreferences.getUserPreferences().numberFormat"
                @update:model-value="setNumberFormat"
              >
                <DropdownMenuRadioItem
                  v-for="opt in numberOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.title }}
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </ModalSection>
  </div>
</template>
